import {calculateErrorMessage} from 'common/helpers'
import {MutableRefObject, useEffect, useState} from 'react'

/**
 * Drives a scrolling voice-note waveform straight from the microphone.
 *
 * Each bar is a loudness snapshot from a *different moment in time*, not a frequency band: the
 * newest sample enters at the right edge and the whole strip scrolls left, so syllables read as an
 * uneven up/down silhouette travelling across the meter — the WhatsApp / voice-note look. Mapping
 * bars to frequency instead makes every bar peak on the same syllable, which just looks like one
 * block rising and falling.
 *
 * The Web Speech API only hands back transcripts — it exposes no amplitude at all — so this opens
 * its own capture and reads an AnalyserNode. Bars are written directly to the DOM as a
 * `--bar-scale` custom property rather than through React state: this runs on every animation
 * frame, and re-rendering the tree 60 times a second would cost the chat panel its frame budget.
 */

// Resting height when nothing is being said, as a fraction of the bar's full height.
const IDLE_SCALE = 0.14
// How much *time* the strip shows end to end, regardless of how many bars fit in it. The sample
// interval is derived from this and the live bar count, so a maximised widget renders the same
// few seconds of speech at higher resolution instead of stretching them into a ~12s window where
// most of the strip is stale idle history.
const VISIBLE_WINDOW_MS = 4200
// Bounds on the derived interval. ~65ms is roughly syllable resolution; below the floor consecutive
// samples start landing inside the same animation frame, above the ceiling speech smears into a
// blob on a very narrow strip.
const MIN_SAMPLE_INTERVAL_MS = 18
const MAX_SAMPLE_INTERVAL_MS = 90

const sampleIntervalForBarCount = (barCount: number): number => {
  if (barCount <= 0) return MAX_SAMPLE_INTERVAL_MS
  const interval = VISIBLE_WINDOW_MS / barCount
  return Math.min(MAX_SAMPLE_INTERVAL_MS, Math.max(MIN_SAMPLE_INTERVAL_MS, interval))
}

/**
 * Stretch or squeeze the sample ring onto a new bar count, keeping the wave that is already on
 * screen. Maximising the widget roughly quintuples the bar count; rebuilding the ring flat would
 * blank the meter mid-sentence, so the existing silhouette is resampled onto the new length and
 * simply drawn at finer detail.
 */
const resampleHistory = (history: number[], length: number): number[] => {
  const previousLength = history.length
  if (previousLength === length) return history
  if (previousLength < 2) return new Array(length).fill(history[0] ?? IDLE_SCALE)

  const next = new Array<number>(length)
  for (let i = 0; i < length; i++) {
    const position = length < 2 ? 0 : (i / (length - 1)) * (previousLength - 1)
    const low = Math.floor(position)
    const high = Math.min(previousLength - 1, low + 1)
    next[i] = history[low] + (history[high] - history[low]) * (position - low)
  }
  return next
}
// Ambient room noise creeps up to this at most, so a noisy office can never pin the bars open.
const MAX_NOISE_FLOOR = 0.06
const NOISE_FLOOR_RISE = 0.005
// RMS above the adapted floor at which the gate is fully open.
const GATE_RANGE = 0.05
// Auto-gain: the loudest recent peak sets full height, so a quiet speaker still fills the strip.
// The reference decays each sample so it recovers after a shout instead of staying stuck high.
const MIN_REFERENCE_PEAK = 0.09
const REFERENCE_PEAK_DECAY = 0.985
// A backgrounded tab can hand back an enormous elapsed time; past this we resync instead of
// pushing hundreds of catch-up samples in one frame.
const MAX_CATCHUP_SAMPLES = 10

const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value)

export interface UseMicLevelMeterOptions {
  /** Capture only while true; the stream is torn down as soon as it flips back. */
  enabled: boolean
  /** Bar elements to drive, in render order (index 0 is the leftmost / oldest sample). */
  barsRef: MutableRefObject<Array<HTMLDivElement | null>>
}

export interface UseMicLevelMeterReturn {
  /**
   * True once levels are actually flowing. While false the caller should keep the decorative CSS
   * fallback running — the mic may be denied, unavailable, or still being acquired.
   */
  isReactive: boolean
}

export const useMicLevelMeter = ({
  enabled,
  barsRef,
}: UseMicLevelMeterOptions): UseMicLevelMeterReturn => {
  const [isReactive, setIsReactive] = useState(false)

  useEffect(() => {
    if (!enabled) return
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return

    let cancelled = false
    let stream: MediaStream | null = null
    let audioContext: AudioContext | null = null
    let frameId: number | null = null

    const resetBars = () => {
      barsRef.current.forEach((bar) => bar?.style.removeProperty('--bar-scale'))
    }

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({audio: true})
        if (cancelled) return

        const AudioContextCtor =
          window.AudioContext ||
          (window as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext
        audioContext = new AudioContextCtor()
        if (audioContext.state === 'suspended') await audioContext.resume()
        if (cancelled) return

        // Registered alongside the voice-call captures so the shared teardown helpers can find and
        // release this stream too if the widget is closed from elsewhere.
        try {
          const gw = window as any
          gw.__activeVoiceMediaStreams = gw.__activeVoiceMediaStreams || new Set()
          gw.__activeVoiceAudioContexts = gw.__activeVoiceAudioContexts || new Set()
          gw.__activeVoiceMediaStreams.add(stream)
          gw.__activeVoiceAudioContexts.add(audioContext)
        } catch (err) {
          calculateErrorMessage(err)
        }

        const analyser = audioContext.createAnalyser()
        // 1024 samples is ~21ms at 48kHz — just over one animation frame, so consecutive reads
        // cover the audio continuously and a sharp consonant can't fall between them.
        analyser.fftSize = 1024
        analyser.smoothingTimeConstant = 0
        audioContext.createMediaStreamSource(stream).connect(analyser)

        const timeDomainData = new Uint8Array(analyser.fftSize)
        // Sized to the bar count each frame: one sample per bar, plus the extra the interpolation
        // below slides towards. Starts empty and is filled on the first tick, once the strip has
        // actually been measured.
        let history: number[] = []
        let sampleInterval = MAX_SAMPLE_INTERVAL_MS

        let lastSampleTime = performance.now()
        // Peak seen since the last sample was pushed, so a transient between frames still lands.
        let windowPeak = 0
        let referencePeak = MIN_REFERENCE_PEAK
        let noiseFloor = 0

        const tick = () => {
          frameId = requestAnimationFrame(tick)

          const bars = barsRef.current
          const barCount = bars.length
          if (barCount === 0) return

          // The strip re-derives its bar count from its own width, so this changes whenever the
          // widget is maximised, minimised or the window resized. Carry the visible wave across
          // instead of restarting it, and re-derive the interval so the window stays the same
          // number of seconds at the new density.
          const historyLength = barCount + 2
          if (history.length !== historyLength) {
            history = history.length
              ? resampleHistory(history, historyLength)
              : new Array(historyLength).fill(IDLE_SCALE)
            sampleInterval = sampleIntervalForBarCount(barCount)
          }

          analyser.getByteTimeDomainData(timeDomainData)

          let sumSquares = 0
          let peak = 0
          for (let i = 0; i < timeDomainData.length; i++) {
            const centered = (timeDomainData[i] - 128) / 128
            sumSquares += centered * centered
            const magnitude = centered < 0 ? -centered : centered
            if (magnitude > peak) peak = magnitude
          }
          const rms = Math.sqrt(sumSquares / timeDomainData.length)
          if (peak > windowPeak) windowPeak = peak

          noiseFloor = Math.min(
            MAX_NOISE_FLOOR,
            noiseFloor * (1 - NOISE_FLOOR_RISE) + rms * NOISE_FLOOR_RISE
          )

          const now = performance.now()
          if (now - lastSampleTime > sampleInterval * MAX_CATCHUP_SAMPLES) {
            lastSampleTime = now
          }

          while (now - lastSampleTime >= sampleInterval) {
            referencePeak = Math.max(
              MIN_REFERENCE_PEAK,
              windowPeak,
              referencePeak * REFERENCE_PEAK_DECAY
            )

            // Gate on RMS against the adapted floor so silence flattens the strip completely,
            // then scale the peak against the running reference for the visible height.
            const gate = Math.pow(clamp01((rms - noiseFloor * 1.8) / GATE_RANGE), 0.5)
            const level = Math.pow(clamp01(windowPeak / referencePeak), 0.8) * gate

            history.push(IDLE_SCALE + (1 - IDLE_SCALE) * level)
            history.shift()

            windowPeak = 0
            lastSampleTime += sampleInterval
          }

          // Slide the window by a fraction of one sample each frame. Interpolating by *position*
          // rather than easing each bar towards a target keeps the peaks crisp as they travel —
          // per-bar easing smears them into a blur.
          const progress = clamp01((now - lastSampleTime) / sampleInterval)
          const base = Math.max(0, history.length - (barCount + 1))

          for (let i = 0; i < barCount; i++) {
            const bar = bars[i]
            if (!bar) continue
            // A bar strip longer than the history (possible only if the two constants drift apart)
            // would read past the ring; fall back to the oldest sample instead of writing NaN.
            const current = history[base + i] ?? IDLE_SCALE
            const next = history[base + i + 1] ?? current
            const value = current + (next - current) * progress
            bar.style.setProperty('--bar-scale', value.toFixed(3))
          }
        }

        setIsReactive(true)
        frameId = requestAnimationFrame(tick)
      } catch (err) {
        // Denied, no device, or an unsupported browser — the decorative CSS animation stands in.
        calculateErrorMessage(err)
        if (!cancelled) setIsReactive(false)
      }
    }

    start()

    return () => {
      cancelled = true
      if (frameId !== null) cancelAnimationFrame(frameId)

      const gw = window as any
      if (stream) {
        try {
          stream.getTracks().forEach((track) => track.stop())
          gw.__activeVoiceMediaStreams?.delete(stream)
        } catch (err) {
          calculateErrorMessage(err)
        }
      }
      if (audioContext) {
        try {
          audioContext.close()
          gw.__activeVoiceAudioContexts?.delete(audioContext)
        } catch (err) {
          calculateErrorMessage(err)
        }
      }

      resetBars()
      setIsReactive(false)
    }
  }, [barsRef, enabled])

  return {isReactive}
}

export default useMicLevelMeter
